import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const Followers = ({ authToken }) => {
    const [followers, setFollowers] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [storeId, setStoreId] = useState(null);

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                // Step 1: Get the current provider's store_id
                const meRes = await fetch(`${API_BASE_URL}/admin/me`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                const meData = await meRes.json();

                if (!meRes.ok || !meData.store_id) {
                    throw new Error('Could not identify store ID');
                }

                setStoreId(meData.store_id);

                // Step 2: Fetch followers for this store
                const folksRes = await fetch(`${API_BASE_URL}/store/${meData.store_id}/followers`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                const folksData = await folksRes.json();

                if (folksRes.ok) {
                    setFollowers(folksData.followers || []);
                    setCount(folksData.count || 0);
                } else {
                    setError(folksData.error || 'Failed to load followers');
                }
            } catch (err) {
                setError(err.message || 'Error occurred');
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [authToken]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Store Followers</h2>
                    <p className="text-sm text-gray-500">Manage individuals following your store</p>
                </div>
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100 flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-700 font-bold text-lg">{count}</span>
                    <span className="text-blue-600 text-sm font-medium">Followers</span>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
                    <AlertCircle className="h-5 w-5 mr-3" />
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Follower Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followed Date</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {followers.length === 0 ? (
                            <tr>
                                <td colSpan={2} className="px-6 py-12 text-center text-gray-500">
                                    <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                    <p>You don't have any followers yet.</p>
                                </td>
                            </tr>
                        ) : (
                            followers.map((f, idx) => {
                                // Backend may return follower email as follower_email, user_email, or email
                                const email = f.follower_email || f.user_email || f.email || '';
                                const dateStr = f.created_at || f.followed_at;
                                return (
                                <tr key={f.id || idx} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3 text-indigo-700 font-bold text-xs">
                                                {(email || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-sm font-medium text-gray-900">{email || 'Unknown Email'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                            {dateStr ? new Date(dateStr).toLocaleDateString(undefined, {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            }) : '—'}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Followers;
